import {
  APPLICATION_CHECK_STATUS,
  CHECK_BLOCK_VISUAL_STATE,
} from '../../../../../config/constants'

import './check-item-block-card.scss'
import {
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Typography,
} from '@mui/material'
import moment from 'moment'
import {
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Pending as PendingIcon,
  ExpandMore as ExpandMoreIcon,
} from '@vedmarket/ui-kit'

import {
  COLORS,
} from 'config/constants'

import {
  checkBlockVisualState,
} from '../check-block-utils'

const CheckItemBlockCard = ({
  managerView = false,
  item,
  savedStatus,
  isActiveBlock = false,
  needsClientAttention = false,
  hasContent,
  completionAction,
  children,
}: ApplicationCheckItemBlockCardProps) => {
  const accordionId = useId()
  const summaryId = `application-check-block-${item.kind}-${accordionId}-summary`
  const contentId = `application-check-block-${item.kind}-${accordionId}-content`
  const headerPaddingY = managerView ? 1 : 1.5

  const visual = checkBlockVisualState({ status: item.status, needsClientAttention: needsClientAttention })
  const savedVisual = checkBlockVisualState({
    status: savedStatus ?? item.status,
    needsClientAttention: needsClientAttention,
  })
  const isCompleted = visual === CHECK_BLOCK_VISUAL_STATE.COMPLETED

  const [ expanded, setExpanded ] = useState(isActiveBlock)
  const wasActiveBlock = useRef(isActiveBlock)

  useEffect(() => {
    if (isActiveBlock && !wasActiveBlock.current) {
      setExpanded(true)
    }

    if (!isActiveBlock && wasActiveBlock.current) {
      setExpanded(false)
    }

    wasActiveBlock.current = isActiveBlock
  }, [ isActiveBlock ])

  useEffect(() => {
    if (savedVisual === CHECK_BLOCK_VISUAL_STATE.COMPLETED && !isActiveBlock) {
      setExpanded(false)
    }
  }, [ savedVisual, isActiveBlock, item.kind ])

  let StatusIcon = PendingIcon

  if (isCompleted) {
    StatusIcon = CheckCircleIcon
  } else if (item.status === APPLICATION_CHECK_STATUS.IN_PROGRESS) {
    StatusIcon = EditIcon
  }

  const statusColor = isCompleted ? COLORS.GREEN : COLORS.WARNING
  const icon = (
    <StatusIcon
      role='img'
      aria-label={item.status_label}
      width={24}
      height={24}
      style={{
        display: 'block',
        color: statusColor,
        fill: statusColor,
        flexShrink: 0,
      }}
    />
  )

  const header = (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      mr: hasContent ? 2 : 0,
      flex: 1,
      minWidth: 0,
    }}>
      {icon}
      <Box sx={{
        flex: 1,
        minWidth: 0,
      }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          alignItems: 'center',
          rowGap: 0.25,
          columnGap: 1.5,
        }}>
          <Typography
            variant={managerView ? 'subtitle1' : 'h6'}
            className='application-check-item-block-card__name'
            sx={{
              fontWeight: 500,
              gridColumn: 1,
              gridRow: 1,
            }}
          >
            {item.kind_label}
          </Typography>
          <Chip
            size='small'
            className='application-check-item-block-card__status'
            label={<Typography component='span' variant='inherit'>{item.status_label}</Typography>}
            sx={{
              gridColumn: 2,
              gridRow: '1 / span 2',
              mr: completionAction ? '40px' : 0,
            }}
          />
          {item.updated_at && (
            <Typography
              variant='caption'
              className='application-check-item-block-card__date'
              component='span'
              sx={{
                gridColumn: 1,
                gridRow: 2,
                color: managerView ? 'text.primary' : COLORS.LIGHT_TEXT_SECONDARY,
                whiteSpace: 'nowrap',
              }}
            >
              {moment(item.updated_at).format('DD.MM.YY HH:mm')}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  )

  if (!hasContent) {
    return (
      <Box className='application-check-item-block-card'>
        <Box sx={{ py: headerPaddingY, px: 3, minHeight: '64px', boxSizing: 'border-box' }}>
          {header}
        </Box>
      </Box>
    )
  }

  return (
    <Accordion
      className='application-check-item-block-card'
      sx={{
        margin: 0,
        '&.Mui-expanded': {
          margin: 0,
        },
      }}
      expanded={expanded}
      onChange={(_event, next) => setExpanded(next)}
    >
      <Box className='application-check-item-block-card__header'>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon width={24} height={24} />}
          aria-controls={contentId}
          id={summaryId}
          sx={{
            py: 0,
            px: 3,
            minHeight: '64px',
            borderBottom: 0,
            '&.Mui-expanded': {
              minHeight: '64px',
              borderBottom: 0,
            },
            ...(managerView && {
              '& .MuiAccordionSummary-content.Mui-expanded': {
                margin: '8px 0',
              },
            }),
          }}
          slotProps={{
            expandIconWrapper: {
              sx: {
                width: '24px',
                height: '24px',
                flexShrink: 0,
                alignItems: 'center',
                justifyContent: 'center',
              },
            },
            content: {
              sx: {
                my: headerPaddingY,
                mx: 0,
                '&.Mui-expanded': {
                  my: headerPaddingY,
                  mx: 0,
                },
              },
            },
          }}
        >
          {header}
        </AccordionSummary>
        {completionAction && (
          <Box className='application-check-item-block-card__completion-action'>
            {completionAction}
          </Box>
        )}
      </Box>
      <AccordionDetails
        aria-labelledby={summaryId}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          pt: 1,
          px: 3,
          pb: 3,
        }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  )
}

export default CheckItemBlockCard
